import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, CheckCircle2, Heart } from "lucide-react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

export default function ReviewPage() {
  const { restaurantId, orderId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/v1/public/review/${restaurantId}/${orderId}`)
      .then(res => {
        setInfo(res.data);
        if (res.data.already_reviewed) {
          setRating(res.data.existing_rating || 0);
          setSubmitted(true);
        }
      })
      .catch(() => toast.error("Pedido no encontrado"))
      .finally(() => setLoading(false));
  }, [restaurantId, orderId]);

  const handleSubmit = async () => {
    if (rating === 0) { toast.error("Selecciona una puntuacion"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/v1/public/review/${restaurantId}/${orderId}`, { rating, comment });
      setSubmitted(true);
      toast.success("Gracias por tu evaluacion!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar");
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-pulse text-gray-400">Cargando...</div>
    </div>
  );

  if (!info) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-500">Pedido no encontrado</p>
    </div>
  );

  const order = info.order || {};
  const restName = info.restaurant_name || "Restaurante";
  const orderNum = order.order_number || order.id?.substring(0, 8).toUpperCase() || "";

  if (submitted) return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
      <div className="max-w-sm w-full text-center space-y-5">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">Gracias!</h1>
        <p className="text-gray-600 text-sm">Tu evaluacion nos ayuda a mejorar cada dia.</p>
        <div className="flex justify-center gap-1">
          {[1,2,3,4,5].map(i => (
            <Star key={i} className={`w-7 h-7 ${i <= rating ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} />
          ))}
        </div>
        <p className="text-xs text-gray-400"><Heart className="w-3 h-3 inline text-red-400" /> {restName}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4" data-testid="review-page">
      <div className="max-w-sm w-full space-y-6">
        <div className="text-center space-y-2">
          {info.restaurant_logo_url ? (
            <img src={info.restaurant_logo_url} alt={restName} className="w-16 h-16 rounded-full object-cover mx-auto" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center mx-auto text-white text-2xl font-bold">
              {restName.charAt(0)}
            </div>
          )}
          <h1 className="text-lg font-bold text-gray-900">{restName}</h1>
          <p className="text-sm text-gray-500">Pedido #{orderNum}</p>
        </div>

        <div className="text-center">
          <h2 className="text-base font-bold text-gray-800">Como fue tu experiencia?</h2>
          <p className="text-xs text-gray-500 mt-1">Tu opinion nos ayuda a mejorar</p>
        </div>

        <div className="flex justify-center gap-2" data-testid="star-rating">
          {[1,2,3,4,5].map(i => (
            <button key={i} onClick={() => setRating(i)} onMouseEnter={() => setHoverRating(i)} onMouseLeave={() => setHoverRating(0)} className="transition-transform hover:scale-110" data-testid={`star-${i}`}>
              <Star className={`w-10 h-10 transition-colors ${i <= (hoverRating || rating) ? "text-amber-400 fill-amber-400" : "text-gray-200 hover:text-amber-200"}`} />
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-center text-sm font-medium text-amber-600">
            {rating === 5 ? "Excelente!" : rating === 4 ? "Muy bien!" : rating === 3 ? "Bien" : rating === 2 ? "Regular" : "Malo"}
          </p>
        )}

        {order.items && order.items.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            <p className="text-xs font-bold text-gray-500 uppercase">Tu pedido</p>
            {order.items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-xs text-gray-600">
                <span>{item.quantity}x {item.product_name}</span>
                <span>{(item.quantity * item.unit_price).toFixed(2)}EUR</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t border-gray-200">
              <span>Total</span><span>{order.total?.toFixed(2)}EUR</span>
            </div>
          </div>
        )}

        <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Dejanos un comentario (opcional)..." className="resize-none h-20 text-sm" data-testid="review-comment" />

        <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base font-bold" onClick={handleSubmit} disabled={submitting || rating === 0} data-testid="submit-review-btn">
          {submitting ? "Enviando..." : "Enviar Evaluacion"}
        </Button>
        <p className="text-center text-[10px] text-gray-400">Powered by Kaiso System</p>
      </div>
    </div>
  );
}
